import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Spin, Descriptions, Card, Tabs, Tag } from "antd";
import axios from "axios";

const { TabPane } = Tabs;

const RealStripeTransactionsModal = ({ visible, onClose, order, timezone }) => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("0");
  
  useEffect(() => {
    if (visible && order && order.real_stripe_transactions && order.real_stripe_transactions.length > 0) {
      fetchTransactionDetails();
    }
  }, [visible, order]);
  
  const fetchTransactionDetails = async () => {
    if (!order || !order.real_stripe_transactions || order.real_stripe_transactions.length === 0) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Fetch each transaction's details
      const transactionPromises = order.real_stripe_transactions.map(transId => {
        const token = localStorage.getItem("accessToken");
        return axios.get(`${process.env.REACT_APP_API_BASE_URL}/fetch-data/stripe-transactions/${transId}/`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
      });
      
      const responses = await Promise.all(transactionPromises);
      const transactionData = responses.map(response => response.data);
      setTransactions(transactionData);
    } catch (error) {
      console.error("Error fetching Stripe transaction details:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (key) => {
    setActiveTab(key);
  };
  
  // Format currency values
  const formatCurrency = (value, currency = "USD") => {
    if (value === undefined || value === null) return "$0.00";
    
    // Stripe stores amounts in cents, convert to dollars
    const dollars = value / 100;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(dollars);
  };
  
  // Format date values
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Render transaction status with appropriate tag color
  const renderStatus = (status) => {
    if (!status) return <Tag>Unknown</Tag>;
    
    switch(status.toLowerCase()) {
      case 'succeeded':
        return <Tag color="green">Succeeded</Tag>;
      case 'complete':
        return <Tag color="green">Complete</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'failed':
        return <Tag color="red">Failed</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };
  
  if (!order) return null;
  
  return (
    <Modal show={visible} onHide={onClose} size="xl" centered dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>Stripe Transactions for Order: {order.number}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spin size="large" />
            <p className="mt-3">Loading transaction details...</p>
          </div>
        ) : transactions.length > 0 ? (
          <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
            {transactions.map((transaction, index) => (
              <TabPane tab={`Transaction #${index + 1}`} key={index.toString()}>
                <Card title={`Transaction ID: ${transaction.id}`} className="mb-4">
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="Status">{renderStatus(transaction.status)}</Descriptions.Item>
                    <Descriptions.Item label="Type">{transaction.type}</Descriptions.Item>
                    <Descriptions.Item label="Charge ID">{transaction.charge_id}</Descriptions.Item>
                    <Descriptions.Item label="Amount">{formatCurrency(transaction.amount, transaction.currency)}</Descriptions.Item>
                    <Descriptions.Item label="Net Amount">{formatCurrency(transaction.net_amount, transaction.currency)}</Descriptions.Item>
                    <Descriptions.Item label="Fee Amount">{formatCurrency(transaction.fee_amount, transaction.currency)}</Descriptions.Item>
                    <Descriptions.Item label="Currency">{transaction.currency?.toUpperCase()}</Descriptions.Item>
                    <Descriptions.Item label="Created Date">{formatDate(transaction.created_date)}</Descriptions.Item>
                    <Descriptions.Item label="Available Date">{transaction.available_date ? formatDate(transaction.available_date) : "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Description">{transaction.description || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Refunded">{transaction.refunded ? "Yes" : "No"}</Descriptions.Item>
                    <Descriptions.Item label="Disputed">{transaction.disputed ? "Yes" : "No"}</Descriptions.Item>
                  </Descriptions>
                  
                  {transaction.payment_method_details && (
                    <>
                      <h5 className="mt-4">Payment Method Details</h5>
                      <Descriptions bordered column={2}>
                        <Descriptions.Item label="Type">{transaction.payment_method_type || "N/A"}</Descriptions.Item>
                        <Descriptions.Item label="Payment Method ID">{transaction.payment_method_id || "N/A"}</Descriptions.Item>
                      </Descriptions>
                    </>
                  )}
                  
                  {transaction.billing_details && (
                    <>
                      <h5 className="mt-4">Billing Details</h5>
                      <Descriptions bordered column={2}>
                        <Descriptions.Item label="Name">{transaction.billing_details.name || "N/A"}</Descriptions.Item>
                        <Descriptions.Item label="Email">{transaction.billing_details.email || "N/A"}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{transaction.billing_details.phone || "N/A"}</Descriptions.Item>
                        {transaction.billing_details.address && (
                          <Descriptions.Item label="Address">
                            {[
                              transaction.billing_details.address.line1,
                              transaction.billing_details.address.line2,
                              transaction.billing_details.address.city,
                              transaction.billing_details.address.state,
                              transaction.billing_details.address.postal_code,
                              transaction.billing_details.address.country
                            ].filter(Boolean).join(", ")}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </>
                  )}
                  
                  {transaction.gateway_account_name && (
                    <>
                      <h5 className="mt-4">Gateway Information</h5>
                      <Descriptions bordered column={1}>
                        <Descriptions.Item label="Gateway Account">{transaction.gateway_account_name}</Descriptions.Item>
                        <Descriptions.Item label="Brand">{transaction.brand_name}</Descriptions.Item>
                      </Descriptions>
                    </>
                  )}
                </Card>
              </TabPane>
            ))}
          </Tabs>
        ) : (
          <p className="text-muted">No Stripe transaction details available.</p>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RealStripeTransactionsModal;
